import { DateRange, isoDateLocal } from './aggregate';
import type { ProcedenciaClientes } from './datosCierre';
import type { MetaFila, MetaGeneraciones, MetaResponse, MetaSerieDia } from '../types/indicadores.types';

/**
 * Utilidades PURAS del tablero de Presupuesto. Todo lo que decide qué ve
 * dirección vive aquí y se prueba sin render: el rollup, el color del semáforo
 * y el acumulado de la curva.
 */

/** Niveles del segmentado de la tabla. El rollup es client-side sobre las mismas filas. */
export type NivelMeta = 'cadena' | 'regional' | 'supervisor' | 'tienda';

/**
 * Nombre comercial de cada cadena, para el nivel Cadena. Copia local a
 * propósito: `CADENA_TO_CHAIN` de `data/stores.ts` no se exporta y va en el
 * sentido contrario.
 */
export const NOMBRE_CADENA: Record<string, string> = {
  VAZ: 'VAZZA',
  TAF: 'FLEXI',
  MRH: 'MARCHA',
  TRE: 'TRENDER',
};

export interface FilaRollup {
  clave: string;
  etiqueta: string;
  /** Sub-etiqueta: para tienda, su cadena; para supervisor, su regional; para cadena, su nombre. */
  detalle: string | null;
  metaMonto: number;
  metaPares: number;
  ventaMonto: number;
  ventaPares: number;
  faltanteMonto: number;
  cumplimientoMonto: number | null;
  cumplimientoPares: number | null;
  tiendas: number;
  /** Tiendas del grupo con meta cargada. Menos que `tiendas` = denominador incompleto. */
  tiendasConMeta: number;

  // --- Composición (fase 1, 27-oct-2026): TODAS las tiendas del grupo, con y
  // sin meta. Contra una API anterior quedan en 0 / null y no se pintan.
  /** Σ (contado + crédito): la venta del grupo, también la de las tiendas sin meta. */
  ventaTotalMonto: number;
  contadoMonto: number;
  creditoMonto: number;
  /** Σ (contado + crédito) de las tiendas que manejan crédito: el denominador «sin TRE». */
  ventaConCreditoMonto: number;
  /** Préstamos personales: no son venta. */
  prestamoMonto: number;
  /** `null` en cuanto una tienda no lo sabe. */
  prestamoTickets: number | null;
  altas: number | null;
  estrenos: number | null;
  /** `true` si alguna tienda del grupo maneja crédito (todas menos TRE). */
  manejaCredito: boolean;
  /** Tiendas del grupo que NO manejan crédito (TRE). */
  tiendasSinCredito: number;
  /** `creditoMonto / ventaConCreditoMonto`; `null` si el grupo no tiene venta con crédito. */
  creditoSobreVentaConCredito: number | null;
}

/**
 * ¿La tienda maneja crédito? Lo dice la API (`manejaCredito`); contra una API
 * anterior, que no lo manda, se deduce igual que ella: todas menos TRE.
 */
export function filaManejaCredito(f: MetaFila): boolean {
  return f.manejaCredito ?? f.chainId !== 'TRE';
}

/** Suma de conteos que pueden faltar: un `null` (o un campo ausente) contagia el total. */
function sumaONula(a: number | null, b: number | null | undefined): number | null {
  return a === null || b === null || b === undefined ? null : a + b;
}

/** Fracción con denominador positivo, o `null`: nunca 0 ni NaN por falta de base. */
function fraccion(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

/** Los campos de composición que comparten el grupo del rollup y el total. */
interface Composicion {
  ventaTotalMonto: number;
  contadoMonto: number;
  creditoMonto: number;
  ventaConCreditoMonto: number;
  prestamoMonto: number;
  prestamoTickets: number | null;
  altas: number | null;
  estrenos: number | null;
  tiendasConCredito: number;
  tiendasSinCredito: number;
}

function composicionVacia(): Composicion {
  return {
    ventaTotalMonto: 0,
    contadoMonto: 0,
    creditoMonto: 0,
    ventaConCreditoMonto: 0,
    prestamoMonto: 0,
    prestamoTickets: 0,
    altas: 0,
    estrenos: 0,
    tiendasConCredito: 0,
    tiendasSinCredito: 0,
  };
}

/**
 * Acumula UNA tienda en la composición. Se llama con TODAS las tiendas, antes
 * del filtro de «con meta»: la venta de VAZ 250, que no tiene meta, es venta a
 * crédito real. Misma regla que la API (`composicionDe` en `meta.util.ts`).
 */
function acumularComposicion(c: Composicion, f: MetaFila): void {
  const contado = f.contadoMonto ?? 0;
  const credito = f.creditoMonto ?? 0;
  c.contadoMonto += contado;
  c.creditoMonto += credito;
  c.ventaTotalMonto += contado + credito;
  c.prestamoMonto += f.prestamoMonto ?? 0;
  c.prestamoTickets = sumaONula(c.prestamoTickets, f.prestamoTickets);
  c.altas = sumaONula(c.altas, f.altas);
  c.estrenos = sumaONula(c.estrenos, f.estrenos);
  if (filaManejaCredito(f)) {
    c.tiendasConCredito += 1;
    c.ventaConCreditoMonto += contado + credito;
  } else {
    c.tiendasSinCredito += 1;
  }
}

/**
 * Agrupa las filas por nivel.
 *
 * ⚠️ El cumplimiento del grupo se calcula sobre los TOTALES del grupo, nunca
 * promediando los porcentajes de las tiendas. Un promedio de porcentajes le da
 * el mismo peso a una tienda de $30.000 que a una de $3.000.000 y saca un
 * número que no es el de nadie.
 *
 * Y sólo suman al numerador y al denominador las tiendas CON meta: incluir la
 * venta de una tienda sin meta inflaría un porcentaje cuyo denominador no la
 * cubre.
 *
 * ⚠️ La COMPOSICIÓN (contado, crédito, préstamos, altas y estrenos) es la
 * excepción a propósito: no tiene meta, así que no hay denominador que inflar,
 * y suma todas las tiendas del grupo. Por eso se acumula ANTES del `continue`.
 */
export function rollupMeta(filas: MetaFila[], nivel: NivelMeta): FilaRollup[] {
  const grupos = new Map<string, { g: FilaRollup; c: Composicion }>();

  for (const f of filas) {
    const { clave, etiqueta, detalle } = claveDe(f, nivel);
    let par = grupos.get(clave);
    if (!par) {
      par = {
        g: {
          clave,
          etiqueta,
          detalle,
          metaMonto: 0,
          metaPares: 0,
          ventaMonto: 0,
          ventaPares: 0,
          faltanteMonto: 0,
          cumplimientoMonto: null,
          cumplimientoPares: null,
          tiendas: 0,
          tiendasConMeta: 0,
          ventaTotalMonto: 0,
          contadoMonto: 0,
          creditoMonto: 0,
          ventaConCreditoMonto: 0,
          prestamoMonto: 0,
          prestamoTickets: 0,
          altas: 0,
          estrenos: 0,
          manejaCredito: false,
          tiendasSinCredito: 0,
          creditoSobreVentaConCredito: null,
        },
        c: composicionVacia(),
      };
      grupos.set(clave, par);
    }
    const { g, c } = par;
    g.tiendas += 1;
    acumularComposicion(c, f);
    if (!f.conMeta) continue;
    g.tiendasConMeta += 1;
    g.metaMonto += f.metaMonto;
    g.metaPares += f.metaPares;
    g.ventaMonto += f.ventaMonto;
    g.ventaPares += f.ventaPares;
  }

  const salida = Array.from(grupos.values()).map(({ g, c }) => {
    g.cumplimientoMonto = g.metaMonto > 0 ? g.ventaMonto / g.metaMonto : null;
    g.cumplimientoPares = g.metaPares > 0 ? g.ventaPares / g.metaPares : null;
    g.faltanteMonto = Math.max(0, g.metaMonto - g.ventaMonto);
    g.ventaTotalMonto = c.ventaTotalMonto;
    g.contadoMonto = c.contadoMonto;
    g.creditoMonto = c.creditoMonto;
    g.ventaConCreditoMonto = c.ventaConCreditoMonto;
    g.prestamoMonto = c.prestamoMonto;
    g.prestamoTickets = c.prestamoTickets;
    g.altas = c.altas;
    g.estrenos = c.estrenos;
    g.manejaCredito = c.tiendasConCredito > 0;
    g.tiendasSinCredito = c.tiendasSinCredito;
    g.creditoSobreVentaConCredito = fraccion(c.creditoMonto, c.ventaConCreditoMonto);
    return g;
  });

  // El orden sigue siendo por la venta CON meta: al cambiar de «Ver: Venta» a
  // «Ver: Crédito» las filas no se mueven de lugar.
  return salida.sort((a, b) => b.ventaMonto - a.ventaMonto);
}

function claveDe(
  f: MetaFila,
  nivel: NivelMeta,
): { clave: string; etiqueta: string; detalle: string | null } {
  if (nivel === 'cadena') {
    return { clave: f.chainId, etiqueta: f.chainId, detalle: NOMBRE_CADENA[f.chainId] ?? null };
  }
  if (nivel === 'regional') {
    const r = f.regional || '(sin regional)';
    return { clave: r, etiqueta: r, detalle: null };
  }
  if (nivel === 'supervisor') {
    const s = f.supervisor || '(sin supervisor)';
    return { clave: s, etiqueta: s, detalle: f.regional || null };
  }
  return {
    clave: `${f.chainId}|${f.branchOfficeId}`,
    etiqueta: f.nombre || `${f.chainId} ${f.branchOfficeId}`,
    detalle: f.chainId,
  };
}

/**
 * Semáforo del cumplimiento, en fracción (1 = meta cumplida).
 *
 * ⚠️ NO reutilizar `tasaColor`: ese tiene la semántica INVERTIDA a propósito
 * (alto = malo, porque mide venta fuera del sistema). Aquí alto es bueno.
 * Umbrales: >=1 verde, >=0,9 dorado, por debajo rojo.
 */
export function metaColor(fraccion: number | null | undefined): string {
  if (fraccion === null || fraccion === undefined || Number.isNaN(fraccion)) {
    return '#9298a3';
  }
  if (fraccion >= 1) return '#17a44d';
  if (fraccion >= 0.9) return '#b7891f';
  return '#d63a33';
}

/** Rango del día de hoy. */
export function todayRange(today: Date = new Date()): DateRange {
  const iso = isoDateLocal(today);
  return { desde: iso, hasta: iso };
}

/**
 * Mes EN CURSO, del día 1 al último del mes.
 *
 * La meta es un instrumento mensual: `previousMonthRange` sirve para mirar
 * atrás, pero no responde «¿cuánto me falta?», que es la pregunta de la vista.
 * El rango llega hasta el fin de mes a propósito, aunque sea futuro: el
 * denominador del mes es la meta del mes completo, y `cmin_met` ya trae los
 * días por venir cargados.
 */
export function currentMonthRange(today: Date = new Date()): DateRange {
  const y = today.getFullYear();
  const m = today.getMonth();
  return {
    desde: isoDateLocal(new Date(y, m, 1)),
    hasta: isoDateLocal(new Date(y, m + 1, 0)),
  };
}

export interface PuntoAcumulado {
  fecha: string;
  /** Meta acumulada hasta ese día. Siempre hay, incluso en días futuros. */
  meta: number;
  /**
   * Venta acumulada hasta ese día, o `null` a partir del primer día sin dato.
   * La curva se CORTA ahí en vez de seguir plana: una línea horizontal en el
   * futuro se lee como «dejaron de vender», que es justo lo contrario.
   */
  venta: number | null;
}

/**
 * Acumula la serie diaria para la curva.
 *
 * En cuanto un día no tiene venta materializada, todos los siguientes vuelven
 * `null`. Arrastrar el último acumulado dibujaría una meseta que nadie vivió.
 */
export function acumular(
  serie: MetaSerieDia[],
  campo: 'monto' | 'pares' = 'monto',
): PuntoAcumulado[] {
  const meta = campo === 'monto' ? 'metaMonto' : 'metaPares';
  const venta = campo === 'monto' ? 'ventaMonto' : 'ventaPares';
  let accMeta = 0;
  let accVenta = 0;
  let cortada = false;

  return serie.map((d) => {
    accMeta += d[meta] ?? 0;
    if (d[venta] === null || d[venta] === undefined) cortada = true;
    if (!cortada) accVenta += d[venta] as number;
    return { fecha: d.fecha, meta: accMeta, venta: cortada ? null : accVenta };
  });
}

/** `YYYY-MM-DD` -> `DD/MM`, igual que el resto del módulo. */
export function diaCorto(fecha: string): string {
  const [, mm, dd] = fecha.split('-');
  return `${dd}/${mm}`;
}

export interface TotalesMeta {
  metaMonto: number;
  metaPares: number;
  ventaMonto: number;
  ventaPares: number;
  cumplimientoMonto: number | null;
  cumplimientoPares: number | null;
  faltanteMonto: number;
  faltantePares: number;
  tiendasConMeta: number;
  tiendasSinMeta: number;
  ventaSinMeta: number;

  // --- Composición (fase 1, 27-oct-2026): todas las filas, con y sin meta. ---
  /** Σ (contado + crédito) = `ventaMonto + ventaSinMeta` (contra la API nueva). */
  ventaTotalMonto: number;
  contadoMonto: number;
  creditoMonto: number;
  prestamoMonto: number;
  prestamoTickets: number | null;
  /** Σ (contado + crédito) de las filas que manejan crédito. */
  ventaConCreditoMonto: number;
  /** `creditoMonto / ventaTotalMonto`; `null` si no hay venta. */
  creditoSobreVenta: number | null;
  /** `creditoMonto / ventaConCreditoMonto` (sin TRE); `null` en un ámbito sólo TRE. */
  creditoSobreVentaConCredito: number | null;
  altas: number | null;
  estrenos: number | null;
  /** Filas que manejan crédito y filas que no (TRE). */
  tiendasConCredito: number;
  tiendasSinCredito: number;
}

/**
 * Totales del ámbito a partir de las filas.
 *
 * POR QUÉ EXISTE, si la API ya manda `totales`: los filtros de Regional,
 * Supervisor y multi-tienda son CLIENT-SIDE (el endpoint sólo acepta una cadena
 * y una sucursal), así que en cuanto se acota por regional hay que recalcular.
 * Tener dos caminos —a veces los totales de la API, a veces los míos— es cómo
 * la cabecera acaba diciendo una cosa y la tabla otra.
 *
 * Así que se calcula SIEMPRE aquí, y una prueba fija que sobre el ámbito
 * completo da exactamente lo que manda la API.
 */
export function totalizarMeta(filas: MetaFila[]): TotalesMeta {
  const conMeta = filas.filter((f) => f.conMeta);
  const sinMeta = filas.filter((f) => !f.conMeta);
  const sum = (xs: MetaFila[], k: keyof MetaFila) =>
    xs.reduce((a, x) => a + (x[k] as number), 0);

  const metaMonto = sum(conMeta, 'metaMonto');
  const metaPares = sum(conMeta, 'metaPares');
  const ventaMonto = sum(conMeta, 'ventaMonto');
  const ventaPares = sum(conMeta, 'ventaPares');

  // La composición, sobre TODAS las filas (con y sin meta), con la misma regla
  // que el rollup y que la API.
  const c = composicionVacia();
  for (const f of filas) acumularComposicion(c, f);

  return {
    metaMonto,
    metaPares,
    ventaMonto,
    ventaPares,
    cumplimientoMonto: metaMonto > 0 ? ventaMonto / metaMonto : null,
    cumplimientoPares: metaPares > 0 ? ventaPares / metaPares : null,
    faltanteMonto: Math.max(0, metaMonto - ventaMonto),
    faltantePares: Math.max(0, metaPares - ventaPares),
    tiendasConMeta: conMeta.length,
    tiendasSinMeta: sinMeta.length,
    ventaSinMeta: sum(sinMeta, 'ventaMonto'),
    ventaTotalMonto: c.ventaTotalMonto,
    contadoMonto: c.contadoMonto,
    creditoMonto: c.creditoMonto,
    prestamoMonto: c.prestamoMonto,
    prestamoTickets: c.prestamoTickets,
    ventaConCreditoMonto: c.ventaConCreditoMonto,
    creditoSobreVenta: fraccion(c.creditoMonto, c.ventaTotalMonto),
    creditoSobreVentaConCredito: fraccion(c.creditoMonto, c.ventaConCreditoMonto),
    altas: c.altas,
    estrenos: c.estrenos,
    tiendasConCredito: c.tiendasConCredito,
    tiendasSinCredito: c.tiendasSinCredito,
  };
}

/**
 * ¿La respuesta trae la composición? `false` contra una API anterior (o tras
 * revertir sólo la API): sin `generaciones` o con alguna fila sin
 * `contadoMonto`. Con `false` la vista queda EXACTAMENTE como antes: sin el
 * bloque, sin «Ver: Venta | Crédito» y sin columnas nuevas en el Excel.
 */
export function hayComposicion(meta: MetaResponse | null | undefined): boolean {
  return !!meta?.generaciones && meta.filas.every((f) => typeof f.contadoMonto === 'number');
}

/**
 * ¿Se pinta el aviso de las cuentas `7C03`? La fecha la decide la API (y lo
 * quita sola el día que se cuenten); aquí sólo se calla en un ámbito sin
 * ninguna tienda con crédito, que es lo que deja un filtro client-side de
 * regional o de tienda sobre TRE.
 */
export function muestraAviso7C03(
  generaciones: MetaGeneraciones | null | undefined,
  filas: MetaFila[],
): boolean {
  return !!generaciones?.avisoCuentas7C03 && filas.some(filaManejaCredito);
}

/**
 * La procedencia de las altas que se puede afirmar del ámbito VISIBLE.
 *
 * La API la manda para el ámbito que recibió (todas o una cadena). La deriva
 * («el ERP hoy: …») y «el correo dijo …» son TOTALES de ese ámbito: en cuanto
 * la cascada client-side acota (regional, supervisor, varias tiendas), sumarlos
 * a las altas visibles daría una cifra que no es la de nadie. Misma regla que
 * Global (`aggregateGlobal`: el correo sólo sin cascada).
 */
export function procedenciaDelAmbito(
  p: ProcedenciaClientes | null | undefined,
  ambitoCompleto: boolean,
): ProcedenciaClientes | null {
  if (!p) return null;
  return ambitoCompleto ? p : { ...p, deriva: null, correo: null };
}
